export const csharpApiExample = `using System;
using System.Net.Http;
using System.Net.Http.Headers;
using System.Security.Cryptography;
using System.Text;
using System.Text.Json;
using System.Text.Json.Serialization;
using System.Threading.Tasks;

namespace ReasonShield.ApiClient
{
    /// <summary>
    /// Client for interacting with the ReasonShield API using dual authentication
    /// </summary>
    public class ReasonShieldClient
    {
        // API configuration constants
        private const string API_BASE_URL = "https://app.reasonshield.com/api/v1";
        private const string API_ENDPOINT_PATH = "/lis-webhook/vendor";
        
        // Authentication credentials (load from environment variables in production)
        private readonly string _vendorId;
        private readonly string _apiKey;
        private readonly string _webhookSecret;
        private readonly HttpClient _httpClient;

        /// <summary>
        /// Initializes a new instance of the ReasonShield client
        /// </summary>
        public ReasonShieldClient(string vendorId, string apiKey, string webhookSecret)
        {
            _vendorId = vendorId ?? throw new ArgumentNullException(nameof(vendorId));
            _apiKey = apiKey ?? throw new ArgumentNullException(nameof(apiKey));
            _webhookSecret = webhookSecret ?? throw new ArgumentNullException(nameof(webhookSecret));
            _httpClient = new HttpClient();
        }

        /// <summary>
        /// Creates an HMAC-SHA256 signature for request payloads
        /// </summary>
        private string GenerateSignature(string payload)
        {
            try
            {
                byte[] keyBytes = Encoding.UTF8.GetBytes(_webhookSecret);
                byte[] messageBytes = Encoding.UTF8.GetBytes(payload);
                
                using (var hmac = new HMACSHA256(keyBytes))
                {
                    byte[] hashBytes = hmac.ComputeHash(messageBytes);
                    return BitConverter.ToString(hashBytes).Replace("-", "").ToLower();
                }
            }
            catch (Exception ex)
            {
                throw new Exception("Failed to generate signature", ex);
            }
        }

        /// <summary>
        /// Submits test data to the ReasonShield API
        /// </summary>
        public async Task<SubmitResponse> SubmitDataAsync(TestData testData)
        {
            try
            {
                // Create the request payload
                var requestPayload = new RequestPayload
                {
                    VendorId = _vendorId,
                    Timestamp = DateTime.UtcNow.ToString("o"),
                    RequestId = Guid.NewGuid().ToString(),
                    TestData = testData
                };

                // Serialize to JSON
                var options = new JsonSerializerOptions
                {
                    PropertyNamingPolicy = JsonNamingPolicy.CamelCase,
                    DefaultIgnoreCondition = JsonIgnoreCondition.WhenWritingNull
                };
                string jsonPayload = JsonSerializer.Serialize(requestPayload, options);
                
                // Generate signature for the serialized payload
                string signature = GenerateSignature(jsonPayload);
                
                // Create HTTP request with both authentication methods
                var content = new StringContent(jsonPayload, Encoding.UTF8, "application/json");
                var request = new HttpRequestMessage(HttpMethod.Post, $"{API_BASE_URL}{API_ENDPOINT_PATH}/submit-data")
                {
                    Content = content
                };
                
                // API Key Authentication
                request.Headers.Authorization = new AuthenticationHeaderValue("Bearer", _apiKey);
                
                // Signature Verification
                request.Headers.Add("X-Vendor-Signature", signature);
                
                // Send request
                HttpResponseMessage response = await _httpClient.SendAsync(request);
                
                // Process response
                string responseBody = await response.Content.ReadAsStringAsync();
                response.EnsureSuccessStatusCode();
                
                // Deserialize response
                return JsonSerializer.Deserialize<SubmitResponse>(responseBody, options);
            }
            catch (HttpRequestException ex)
            {
                throw new Exception($"API request failed: {ex.Message}", ex);
            }
            catch (JsonException ex)
            {
                throw new Exception($"Failed to parse API response: {ex.Message}", ex);
            }
            catch (Exception ex)
            {
                throw new Exception($"An error occurred: {ex.Message}", ex);
            }
        }

        /// <summary>
        /// Checks the status of submitted data
        /// </summary>
        public async Task<StatusResponse> CheckStatusAsync(string dataId)
        {
            try
            {
                // Create HTTP request
                var request = new HttpRequestMessage(HttpMethod.Get, $"{API_BASE_URL}{API_ENDPOINT_PATH}/status/{dataId}");
                
                // API Key Authentication only for GET requests
                request.Headers.Authorization = new AuthenticationHeaderValue("Bearer", _apiKey);
                
                // Send request
                HttpResponseMessage response = await _httpClient.SendAsync(request);
                
                // Process response
                string responseBody = await response.Content.ReadAsStringAsync();
                response.EnsureSuccessStatusCode();
                
                // Deserialize response
                var options = new JsonSerializerOptions { PropertyNamingPolicy = JsonNamingPolicy.CamelCase };
                return JsonSerializer.Deserialize<StatusResponse>(responseBody, options);
            }
            catch (Exception ex)
            {
                throw new Exception($"Failed to check status: {ex.Message}", ex);
            }
        }

        /// <summary>
        /// Retrieves the report for processed data
        /// </summary>
        public async Task<ReportResponse> GetReportAsync(string dataId)
        {
            try
            {
                // Create HTTP request
                var request = new HttpRequestMessage(HttpMethod.Get, $"{API_BASE_URL}{API_ENDPOINT_PATH}/report/{dataId}");
                
                // API Key Authentication only for GET requests
                request.Headers.Authorization = new AuthenticationHeaderValue("Bearer", _apiKey);
                
                // Send request
                HttpResponseMessage response = await _httpClient.SendAsync(request);
                
                // Process response
                string responseBody = await response.Content.ReadAsStringAsync();
                response.EnsureSuccessStatusCode();
                
                // Deserialize response
                var options = new JsonSerializerOptions { PropertyNamingPolicy = JsonNamingPolicy.CamelCase };
                return JsonSerializer.Deserialize<ReportResponse>(responseBody, options);
            }
            catch (Exception ex)
            {
                throw new Exception($"Failed to retrieve report: {ex.Message}", ex);
            }
        }
    }

    // Request and response data models
    
    public class RequestPayload
    {
        [JsonPropertyName("vendor_id")]
        public string VendorId { get; set; }
        
        [JsonPropertyName("timestamp")]
        public string Timestamp { get; set; }
        
        [JsonPropertyName("request_id")]
        public string RequestId { get; set; }
        
        [JsonPropertyName("test_data")]
        public TestData TestData { get; set; }
    }

    public class TestData
    {
        [JsonPropertyName("test_id")]
        public string TestId { get; set; }
        
        [JsonPropertyName("panel_type")]
        public string PanelType { get; set; }
        
        [JsonPropertyName("patient_info")]
        public PatientInfo PatientInfo { get; set; }
        
        [JsonPropertyName("collection_info")]
        public CollectionInfo CollectionInfo { get; set; }
        
        [JsonPropertyName("test_details")]
        public TestDetails TestDetails { get; set; }
    }

    public class PatientInfo
    {
        [JsonPropertyName("patient_id")]
        public string PatientId { get; set; }
        
        [JsonPropertyName("age")]
        public int Age { get; set; }
        
        [JsonPropertyName("gender")]
        public string Gender { get; set; }
    }

    public class CollectionInfo
    {
        [JsonPropertyName("collection_date")]
        public string CollectionDate { get; set; }
        
        [JsonPropertyName("specimen_type")]
        public string SpecimenType { get; set; }
    }

    public class TestDetails
    {
        [JsonPropertyName("viruses")]
        public PathogenResult[] Viruses { get; set; }
        
        [JsonPropertyName("bacteria")]
        public PathogenResult[] Bacteria { get; set; }
    }

    public class PathogenResult
    {
        [JsonPropertyName("name")]
        public string Name { get; set; }
        
        [JsonPropertyName("result")]
        public string Result { get; set; }
    }

    public class SubmitResponse
    {
        [JsonPropertyName("data_id")]
        public string DataId { get; set; }
        
        [JsonPropertyName("status")]
        public string Status { get; set; }
        
        [JsonPropertyName("message")]
        public string Message { get; set; }
        
        [JsonPropertyName("timestamp")]
        public string Timestamp { get; set; }
    }

    public class StatusResponse
    {
        [JsonPropertyName("data_id")]
        public string DataId { get; set; }
        
        [JsonPropertyName("status")]
        public string Status { get; set; }
        
        [JsonPropertyName("message")]
        public string Message { get; set; }
        
        [JsonPropertyName("timestamp")]
        public string Timestamp { get; set; }
        
        [JsonPropertyName("report_available")]
        public bool ReportAvailable { get; set; }
        
        [JsonPropertyName("report_url")]
        public string ReportUrl { get; set; }
    }

    public class ReportResponse
    {
        [JsonPropertyName("data_id")]
        public string DataId { get; set; }
        
        [JsonPropertyName("report_data")]
        public ReportData ReportData { get; set; }
    }

    public class ReportData
    {
        [JsonPropertyName("test_id")]
        public string TestId { get; set; }
        
        [JsonPropertyName("analysis_date")]
        public string AnalysisDate { get; set; }
        
        [JsonPropertyName("risk_assessment")]
        public RiskAssessment RiskAssessment { get; set; }
    }

    public class RiskAssessment
    {
        [JsonPropertyName("overall_risk")]
        public string OverallRisk { get; set; }
    }

    // Example usage
    class Program
    {
        static async Task Main(string[] args)
        {
            try
            {
                // Get authentication credentials (in production, load from environment variables)
                string vendorId = Environment.GetEnvironmentVariable("VENDOR_ID") ?? "your-vendor-id";
                string apiKey = Environment.GetEnvironmentVariable("VENDOR_API_KEY") ?? "your-api-key";
                string webhookSecret = Environment.GetEnvironmentVariable("VENDOR_WEBHOOK_SECRET") ?? "your-webhook-secret";
                
                // Initialize the ReasonShield client
                var client = new ReasonShieldClient(vendorId, apiKey, webhookSecret);
                
                // Create test data
                var testData = new TestData
                {
                    TestId = "12345",
                    PanelType = "RESPIRATORY",
                    PatientInfo = new PatientInfo
                    {
                        PatientId = "PT-789456",
                        Age = 45,
                        Gender = "M"
                    },
                    CollectionInfo = new CollectionInfo
                    {
                        CollectionDate = "2024-09-10T13:11:00-06:00",
                        SpecimenType = "nasopharyngeal swab"
                    },
                    TestDetails = new TestDetails
                    {
                        Viruses = new[]
                        {
                            new PathogenResult { Name = "Influenza A", Result = "Not Detected" },
                            new PathogenResult { Name = "SARS-CoV-2", Result = "Detected" }
                        },
                        Bacteria = new[]
                        {
                            new PathogenResult { Name = "Streptococcus pneumoniae", Result = "Not Detected" },
                            new PathogenResult { Name = "Mycoplasma pneumoniae", Result = "Not Detected" }
                        }
                    }
                };
                
                // Submit the data
                Console.WriteLine("Submitting test data...");
                SubmitResponse submitResponse = await client.SubmitDataAsync(testData);
                Console.WriteLine($"Data submitted successfully. ID: {submitResponse.DataId}");
                
                // Get the data ID
                string dataId = submitResponse.DataId;
                
                // Check status (in a real implementation, you'd add a delay or polling mechanism)
                Console.WriteLine("Checking status...");
                StatusResponse statusResponse = await client.CheckStatusAsync(dataId);
                Console.WriteLine($"Status: {statusResponse.Status}");
                
                // If processing is complete and report is available, get the report
                if (statusResponse.Status == "COMPLETED" && statusResponse.ReportAvailable)
                {
                    Console.WriteLine("Retrieving report...");
                    ReportResponse reportResponse = await client.GetReportAsync(dataId);
                    Console.WriteLine($"Retrieved report with risk assessment: {reportResponse.ReportData.RiskAssessment.OverallRisk}");
                }
                else
                {
                    Console.WriteLine("Report not yet available.");
                }
            }
            catch (Exception ex)
            {
                Console.WriteLine($"Error: {ex.Message}");
                if (ex.InnerException != null)
                {
                    Console.WriteLine($"Inner error: {ex.InnerException.Message}");
                }
            }
        }
    }
}`; 